<template>
  <div class="tw-rounded-sm tw-bg-white tw-px-4 tw-py-3 tw-h-full tw-min-h-fit">
    <div class="tw-flex">
      <div class="tw-grow tw-pb-2">
        <div class="tw-text-lg tw-font-semibold">
          {{ t("customer-usage-title") }}
        </div>
        <div
          class="tw-text-xs tw-font-medium tw-uppercase tw-text-primary-grey-400"
        >
          {{ t("customer-usage-value") }}
        </div>
      </div>
      <span v-tippy="tooltip" class="tw-text-primary-grey-200 tw-mt-3">
        <cb-icon-information :width="18" />
      </span>
    </div>
    <template v-if="usageStats.length > 0 && !loading">
      <div class="tw-h-3/4 tw-overflow-y-auto">
        <div
          v-for="(usage, idx) in usageStats"
          :key="idx"
          class="tw-mb-1.5 tw-mr-3"
        >
          <div class="tw-flex tw-flex-row tw-justify-between tw-mb-1">
            <div class="tw-text-xs tw-font-semibold">
              {{ usage.name }}
            </div>
            <div class="tw-text-xs">
              {{ usage.seats_used }}/{{ usage.seats_purchased }}
            </div>
          </div>
          <div class="tw-h-3 tw-w-full tw-bg-primary-grey-50 tw-rounded-full">
            <div
              class="tw-h-3 tw-rounded-full"
              :class="lineColourBasedOnService(usage)"
              :style="{
                width: widthBasedOnService(usage),
              }"
            ></div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="usageStats.length === 0 && !loading">
      <div class="tw-grid tw-place-items-center">
        <svg
          width="auto"
          height="100"
          viewBox="0 0 180 135"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M23.3701 40.837C35.8454 34.2663 69.9471 33.8543 77.0166 51.514C84.0865 69.1737 140.867 58.1091 149.435 70.243C158.002 82.3769 140.516 84.4819 161.809 95.3127C183.101 106.144 168.888 126.666 166.186 126.666C163.485 126.666 18.466 126.666 18.466 126.666C18.466 126.666 26.0809 107.339 14.039 89.8977C1.99716 72.457 10.8949 47.4078 23.3701 40.837Z"
            fill="#E7E8E9"
          />
          <path
            d="M88.9571 36.0838L58.7788 24.0205C57.0694 23.3371 55.1297 24.169 54.4463 25.8784C53.763 27.5878 54.5948 29.5276 56.3043 30.2109L86.4825 42.2742C88.192 42.9575 90.1317 42.1257 90.815 40.4162C91.4983 38.7068 90.6665 36.7671 88.9571 36.0838Z"
            fill="#BBE2DA"
          />
          <path
            d="M141.522 22.323L125.236 8.03253C123.852 6.8183 121.746 6.95573 120.532 8.33948C119.318 9.72323 119.455 11.8293 120.839 13.0435L137.125 27.334C138.508 28.5483 140.614 28.4108 141.829 27.0271C143.043 25.6433 142.905 23.5373 141.522 22.323Z"
            fill="#BBE2DA"
          />
          <path
            d="M92.5876 42.2893L123.814 14.703C125.194 13.4842 125.324 11.3776 124.106 9.99796C122.887 8.61827 120.78 8.48788 119.401 9.70672L88.1738 37.293C86.7942 38.5118 86.6638 40.6184 87.8826 41.9981C89.1014 43.3777 91.208 43.5081 92.5876 42.2893Z"
            fill="#BBE2DA"
          />
          <path
            d="M25.0666 61.385L58.7448 29.0809C60.0734 27.8065 60.1173 25.6964 58.843 24.3679C57.5686 23.0393 55.4585 22.9953 54.1299 24.2697L20.4517 56.5738C19.1232 57.8482 19.0792 59.9583 20.3536 61.2869C21.6279 62.6154 23.738 62.6594 25.0666 61.385Z"
            fill="#BBE2DA"
          />
          <path d="M66.666 55H46.666V126.667H66.666V55Z" fill="#76C6B5" />
          <path
            d="M49.166 50.834H64.166L66.666 55.0007H46.666L49.166 50.834Z"
            fill="#97D3C6"
          />
          <path d="M100 73.334H80V126.667H100V73.334Z" fill="#76C6B5" />
          <path
            d="M82.5 69.166H97.5L100 73.3327H80L82.5 69.166Z"
            fill="#97D3C6"
          />
          <path d="M133.334 35H113.334V126.667H133.334V35Z" fill="#76C6B5" />
          <path
            d="M115.834 30.834H130.834L133.334 35.0007H113.334L115.834 30.834Z"
            fill="#97D3C6"
          />
          <path
            d="M56.666 32.5C59.4274 32.5 61.666 30.2614 61.666 27.5C61.666 24.7386 59.4274 22.5 56.666 22.5C53.9046 22.5 51.666 24.7386 51.666 27.5C51.666 30.2614 53.9046 32.5 56.666 32.5Z"
            fill="#76C6B5"
          />
          <path
            d="M90 44.166C92.7614 44.166 95 41.9274 95 39.166C95 36.4046 92.7614 34.166 90 34.166C87.2386 34.166 85 36.4046 85 39.166C85 41.9274 87.2386 44.166 90 44.166Z"
            fill="#76C6B5"
          />
          <path
            d="M123.334 16.666C126.095 16.666 128.334 14.4274 128.334 11.666C128.334 8.90459 126.095 6.66602 123.334 6.66602C120.573 6.66602 118.334 8.90459 118.334 11.666C118.334 14.4274 120.573 16.666 123.334 16.666Z"
            fill="#76C6B5"
          />
        </svg>
        <div class="tw-font-semibold">
          {{
            t("customer-usage-chart", {
              solution: selectedSolutionName,
            })
          }}
        </div>
        <div class="tw-text-sm tw-font-medium tw-text-primary-grey-400">
          {{ t("subtext-customer-usage-chart") }}
        </div>
      </div>
    </template>
    <template v-if="loading">
      <div v-for="i in [0, 1, 2, 3]" :key="i" class="tw-mb-1.5 tw-mr-3">
        <div class="tw-flex tw-flex-row tw-justify-between tw-mb-1">
          <div
            class="tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50 tw-h-3 tw-w-2/5"
          />
        </div>
        <div class="tw-h-3 tw-w-full tw-bg-primary-grey-50 tw-rounded">
          <div
            class="tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50 tw-h-1 tw-w-full"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { UsagePerCustomerResponse } from "@/types/serviceUsage.ts";
import {
  Licence,
  OrganisationWithLicencesResponse,
} from "@/types/licenceSolutions.ts";

interface UsagePerCustomerProps {
  selectedServiceUuid: string;
  selectedSolutionName: string;
}

const props = defineProps<UsagePerCustomerProps>();
const { selectedServiceUuid, selectedSolutionName } = toRefs(props);

const { t } = useI18n();
const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const loading = ref<boolean>(true);
const tooltip = ref({
  content: t("customer-usage-tooltip"),
  placement: "top",
});
const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);
const usageStats = ref<UsagePerCustomerResponse[] | []>([]);

const widthBasedOnService = (service: UsagePerCustomerResponse) => {
  const num = service.seats_utilized_ratio! * 100;
  const percentage = (service.seats_used! / service.seats_purchased!) * 100;
  if (num === 0) {
    return "0%";
  }
  return num > 100 ? "100%" : `${percentage}%`;
};
const lineColourBasedOnService = (service: UsagePerCustomerResponse) => {
  const threshold = 0.8 * service.seats_purchased!;
  const lineClass =
    service.seats_used! >= threshold
      ? "tw-bg-[#fc842d]"
      : "tw-bg-primary-grey-300";
  return service.seats_used! > service.seats_purchased!
    ? "tw-bg-[#d23f3f]"
    : lineClass;
};

const arrangeAccounts = (
  items: OrganisationWithLicencesResponse[],
  selectedServiceUuid: string,
): UsagePerCustomerResponse[] => {
  // TODO: this will be redundand once API `service_uuid` param is fixed:
  const filteredAccountsByService = items.filter(
    (item) =>
      (item.licence?.solutions?.flat?.() || []).some(
        (licence: Licence) => licence.solution_uuid === selectedServiceUuid,
      ) ?? false,
  );

  return filteredAccountsByService
    .map((org: OrganisationWithLicencesResponse) => {
      return {
        name: org.name,
        // seats_issued: org.licence.seats_issued || 0, TODO: this is not in the API response!
        seats_purchased: org.licence.seats_purchased || 0,
        seats_used: org.licence.seats_used || 0,
        seats_used_reported_at: org.licence.seats_used_reported_at,
        seats_utilized_ratio: org.licence.seats_utilized_ratio,
        percent: `${org.licence.seats_utilized_ratio * 100}%`,
      };
    })
    .sort((a, b) => b.seats_utilized_ratio - a.seats_utilized_ratio);
};

watch(selectedServiceUuid, async (selectedUuid: string) => {
  loading.value = true;
  await organisationsStore.getCustomerPerUsageOrg({
    parent_uuid: selectedMsp.value?.uuid || selectedAccount.value?.uuid,
    solution_uuid: selectedUuid,
  });
  loading.value = false;
});

watch(
  () => organisationsStore.orgsPerCustomer,
  (newData) => {
    loading.value = true;
    usageStats.value = arrangeAccounts(
      newData as unknown as Parameters<typeof arrangeAccounts>[0],
      selectedServiceUuid.value,
    );
    loading.value = false;
  },
);
</script>
